import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Phase = 'idle' | 'typing' | 'submitted' | 'calling' | 'done';

const TYPE_INTERVAL_MS = 32;
const ENTER_PAUSE_MS = 380;
const CALL_PAUSE_MS = 520;
const LOAD_DURATION_MS = 2600;

function prefersReducedMotion(): boolean {
  if (typeof matchMedia === 'undefined') return false;
  return matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Mac-styled terminal that mimics a coding-agent session calling AgentBrush
 * via MCP. Rendered as a `<div>` (not `<section>`) so the landing page's
 * landmark contract — verified by landing-page.test.tsx — stays at two.
 */
export function AgentDemoSection() {
  const { t } = useTranslation('landing');

  const prompt = t('agentDemo.userPrompt');
  const reduceMotion = useMemo(prefersReducedMotion, []);

  const wrapRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);
  const [phase, setPhase] = useState<Phase>(reduceMotion ? 'done' : 'idle');
  const [typed, setTyped] = useState(reduceMotion ? prompt.length : 0);

  // Start the sequence the first time the block scrolls into view.
  useEffect(() => {
    if (reduceMotion || started) return;
    const node = wrapRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setStarted(true);
          obs.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    obs.observe(node);
    return () => {
      obs.disconnect();
    };
  }, [reduceMotion, started]);

  // Drive the phase state machine.
  useEffect(() => {
    if (!started || reduceMotion) return;
    const timers: ReturnType<typeof setTimeout>[] = [];

    setPhase('typing');
    for (let i = 1; i <= prompt.length; i++) {
      timers.push(setTimeout(() => setTyped(i), i * TYPE_INTERVAL_MS));
    }
    const typeEnd = prompt.length * TYPE_INTERVAL_MS;
    timers.push(setTimeout(() => setPhase('submitted'), typeEnd + ENTER_PAUSE_MS));
    timers.push(setTimeout(() => setPhase('calling'), typeEnd + ENTER_PAUSE_MS + CALL_PAUSE_MS));
    timers.push(
      setTimeout(
        () => setPhase('done'),
        typeEnd + ENTER_PAUSE_MS + CALL_PAUSE_MS + LOAD_DURATION_MS,
      ),
    );

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [started, reduceMotion, prompt.length]);

  const typedSlice = phase === 'typing' ? prompt.slice(0, typed) : prompt;
  const showPromptLine = phase !== 'idle';
  const showCallLine = phase === 'calling' || phase === 'done';
  const showDoneLine = phase === 'done';
  const imageVisible = phase === 'done';

  return (
    <div className="mx-auto w-full max-w-[1280px] px-6 py-20" ref={wrapRef}>
      {/* Eyebrow + heading — centered like the other landing blocks */}
      <div className="mx-auto max-w-3xl text-center">
        <span className="font-mono text-caption uppercase tracking-[0.25em] text-accent-cyan">
          {t('agentDemo.eyebrow')}
        </span>
        <h2 className="mt-3 text-[clamp(2rem,4vw,3rem)] font-semibold leading-[1.1] tracking-[-0.02em] text-primary">
          <span className="agb-shine-text">{t('agentDemo.heading')}</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-body text-secondary">
          {t('agentDemo.subheadline')}
        </p>
      </div>

      {/* Stage: terminal nudged off-center to the left, image overflowing
          bottom-right. On smaller viewports we collapse to a column so the
          image sits below without clipping. */}
      <div className="relative mx-auto mt-14 max-w-[1180px]">
        <div className="lg:pr-[18%]">
          <div className="relative lg:-translate-x-2">
            {/* Outer glow ring */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-px rounded-xl"
              style={{
                background:
                  'linear-gradient(140deg, rgba(45,225,252,0.35), rgba(37,110,255,0.2) 40%, transparent 70%)',
                padding: 1,
                WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              }}
            />
            <div className="relative overflow-hidden rounded-xl border border-white/[0.06] bg-surface shadow-[0_40px_120px_-30px_rgba(37,110,255,0.35)]">
              {/* macOS title bar — compact */}
              <div className="flex items-center gap-2 border-b border-white/[0.06] px-3.5 py-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-accent-red/80" role="presentation" />
                <div className="h-2.5 w-2.5 rounded-full bg-[#f5c542]/80" role="presentation" />
                <div className="h-2.5 w-2.5 rounded-full bg-accent-lime/80" role="presentation" />
                <span className="mx-auto -translate-x-4 font-mono text-[10px] text-muted">
                  <span className="sr-only">Terminal · </span>
                  {t('agentDemo.windowTitle')}
                </span>
              </div>

              {/* Terminal body */}
              <pre
                className="relative min-h-[260px] px-6 py-5 font-mono text-mono leading-relaxed sm:min-h-[300px]"
                aria-live="polite"
              >
                {/* Welcome banner (kept generic — no product naming) */}
                <div className="text-accent-cyan">
                  ╭─ agent session ─────────────────────────────╮
                </div>
                <div className="text-secondary">│ ready · type a request, press enter │</div>
                <div className="mb-3 text-accent-cyan">
                  ╰─────────────────────────────────────────────╯
                </div>

                {/* User prompt line */}
                <div className="text-primary">
                  <span className="text-accent-lime">{'> '}</span>
                  {showPromptLine ? (
                    <span>{typedSlice}</span>
                  ) : (
                    <span className="text-muted">…</span>
                  )}
                  {phase === 'typing' && <span className="agb-terminal-caret" />}
                </div>

                {/* MCP call line — appears after Enter */}
                {showCallLine && (
                  <div className="mt-3 text-accent-cyan">
                    {t('agentDemo.mcpCall')}{' '}
                    {phase === 'calling' && (
                      <span className="agb-terminal-dots" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                      </span>
                    )}
                  </div>
                )}

                {/* Done line + token cost */}
                {showDoneLine && (
                  <>
                    <div className="mt-2 text-accent-lime">{t('agentDemo.mcpDone')}</div>
                    <div className="text-secondary"> 1024×1024 · 6 tokens · stage 2 · refined</div>
                  </>
                )}

                {/* Trailing caret when nothing else is animating */}
                {(phase === 'submitted' || phase === 'done') && (
                  <span className="agb-terminal-caret-static" />
                )}
              </pre>
            </div>
          </div>
        </div>

        {/* Generated image — bottom-right, overflowing the terminal.
            `pointer-events-none` so it never blocks the terminal text. */}
        <div
          aria-hidden={!imageVisible}
          className={`pointer-events-none absolute right-0 z-10 transition-all duration-700 ease-out ${
            imageVisible ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
          }`}
          style={{
            bottom: '-9%',
            width: 'clamp(180px, 32vw, 360px)',
          }}
        >
          <div className="relative">
            {/* Soft halo behind the mascot */}
            <div
              aria-hidden="true"
              className="absolute inset-0 -z-10 blur-3xl"
              style={{
                background:
                  'radial-gradient(circle at 50% 55%, rgba(45,225,252,0.35), transparent 65%)',
              }}
            />
            <img
              src="/robot-mascot.png"
              alt={t('agentDemo.imageAlt')}
              className="h-auto w-full drop-shadow-[0_30px_60px_rgba(0,0,0,0.55)]"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes agb-terminal-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .agb-terminal-caret,
        .agb-terminal-caret-static {
          display: inline-block;
          width: 0.6ch;
          height: 1em;
          margin-left: 0.1em;
          vertical-align: -0.12em;
          background: var(--color-accent-lime);
          animation: agb-terminal-blink 1s step-end infinite;
        }
        @keyframes agb-terminal-dot {
          0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-2px); }
        }
        .agb-terminal-dots {
          display: inline-flex;
          gap: 4px;
          margin-left: 6px;
          vertical-align: middle;
        }
        .agb-terminal-dots span {
          width: 5px;
          height: 5px;
          border-radius: 9999px;
          background: var(--color-accent-cyan);
          display: inline-block;
          animation: agb-terminal-dot 1.1s ease-in-out infinite;
        }
        .agb-terminal-dots span:nth-child(2) { animation-delay: 0.18s; }
        .agb-terminal-dots span:nth-child(3) { animation-delay: 0.36s; }

        @media (prefers-reduced-motion: reduce) {
          .agb-terminal-caret,
          .agb-terminal-caret-static,
          .agb-terminal-dots span { animation: none; }
        }
      `}</style>
    </div>
  );
}
